<template>
  <div class="min-h-screen bg-slate-50 py-10">
    <Head :title="`Estado de Cuenta: ${client.name}`" />

    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Top header / navbar -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 pb-4 border-b border-slate-200">
        <div class="flex items-center space-x-4 mb-4 sm:mb-0">
          <button 
            @click="back()"
            class="inline-flex items-center px-3.5 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm transition-colors duration-150"
            title="Volver a la consulta"
          >
            <svg class="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          <logo :src="logo" class="h-10 w-auto object-contain hidden sm:block mr-2" />
          <div>
            <h1 class="text-2xl font-black text-gray-900 leading-tight">Estado de Cuenta Público</h1>
            <p class="text-sm text-gray-500 font-medium">Inmueble: {{ client.companyName || 'N/A' }} | Cédula/Alias: {{ client.identification }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <div class="text-sm font-bold text-gray-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3.5 py-2 flex items-center">
            <span class="w-2.5 h-2.5 bg-indigo-600 rounded-full mr-2"></span>
            Propietario: {{ client.name }}
          </div>
          <a
            v-if="is_morochas"
            :href="`/actualizar-datos?client_id=${client.id}`"
            class="inline-flex items-center text-xs font-bold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 border border-indigo-300 rounded-lg px-3 py-2 transition-colors shadow-sm"
            title="Actualizar correo electrónico y teléfono"
          >
            <svg class="h-4 w-4 mr-1.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Actualizar Mis Datos
          </a>
        </div>
      </div>

      <!-- KPI Statistics Cards -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <!-- 1. Cuánto Debe (Total Outstanding Debt) -->
        <div class="bg-red-600 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-red-100 uppercase tracking-wider">Total Adeudado</dt>
                  <dd class="text-3xl font-black mt-1">${{ parseFloat(client.balance).toFixed(2) }}</dd>
                  <dd v-if="change > 0" class="text-xs font-bold text-red-100 mt-1">Bs. {{ (parseFloat(client.balance) * change).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Cuotas Pendientes -->
        <div class="bg-yellow-500 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-yellow-100 uppercase tracking-wider">Cuotas Pendientes</dt>
                  <dd class="text-3xl font-black mt-1">{{ orders.length }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Cuotas Pagadas -->
        <div class="bg-green-600 overflow-hidden shadow-sm rounded-xl text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white/20 rounded-lg p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-xs font-semibold text-green-100 uppercase tracking-wider">Cuotas Pagadas (Al día)</dt>
                  <dd class="text-3xl font-black mt-1">{{ paid_orders_count || 0 }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pendings Table Details -->
      <div class="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden mb-8">
        <div class="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
          <h2 class="text-lg font-bold text-gray-900 flex items-center">
            <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Detalle de Cuotas por Cobrar (Facturación Pendiente)
          </h2>
          <Link
            v-if="orders.length > 0"
            :href="`/getOrderExternal/${orders[0].id}`"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-bold rounded-lg text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
          >
            <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Reportar Pago
          </Link>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">N° Cuota</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Concepto</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Abonado</th>
                <th scope="col" class="px-3 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Restante</th>
                <th scope="col" class="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Acción</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="order in orders" 
                :key="order.id" 
                class="hover:bg-slate-50/50 transition-colors"
              >
                <td class="px-4 py-3.5 whitespace-nowrap">
                  <Link 
                    :href="`/getOrderExternal/${order.id}`" 
                    class="text-sm font-bold text-indigo-600 hover:text-indigo-900 flex items-center"
                    title="Ver detalle del mes o reportar pago de esta alícuota"
                  >
                    #{{ order.number_order }}
                    <svg class="h-3.5 w-3.5 ml-1 text-indigo-400 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </Link>
                </td>
                <td class="px-3 py-3.5 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(order.date) }}
                </td>
                <td class="px-3 py-3.5 text-sm text-gray-600 max-w-xs truncate">
                  {{ order.comentary || 'Cuota de condominio' }}
                </td>
                <td class="px-3 py-3.5 whitespace-nowrap text-sm font-semibold text-gray-900">
                  ${{ parseFloat(order.total).toFixed(2) }}
                </td>
                <td class="px-3 py-3.5 whitespace-nowrap text-sm text-green-600 font-semibold">
                  ${{ parseFloat(order.balance).toFixed(2) }}
                </td>
                <td class="px-3 py-3.5 whitespace-nowrap text-right">
                  <div class="text-sm font-bold text-red-600">${{ (order.total - order.balance).toFixed(2) }}</div>
                  <div v-if="change > 0" class="text-xs text-gray-500 font-medium">Bs. {{ ((order.total - order.balance) * change).toFixed(2) }}</div>
                </td>
                <td class="px-4 py-3.5 whitespace-nowrap text-center">
                  <span v-if="order.order_state === 'payRevition'" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                    <svg class="w-3.5 h-3.5 mr-1 animate-pulse text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pago en Revisión
                  </span>
                  <Link
                    v-else
                    :href="`/getOrderExternal/${order.id}`"
                    class="inline-flex items-center px-3.5 py-1.5 border border-transparent text-xs font-bold rounded-lg text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
                  >
                    <svg class="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Reportar Pago
                  </Link>
                </td>
              </tr>
              <tr v-if="orders.length === 0">
                <td colspan="7" class="px-6 py-12 text-center text-sm text-gray-500 bg-slate-50/50">
                  <svg class="h-12 w-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="font-bold text-gray-900">¡Tu cuenta se encuentra al día!</p>
                  <p class="text-xs text-gray-500 mt-1">No posees cuotas vencidas ni deudas de condominio registradas en este momento.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Payments Table Details -->
      <div class="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden mb-8">
        <div class="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h2 class="text-lg font-bold text-gray-900 flex items-center">
            <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Historial de Pagos y Descarga de Recibos
          </h2>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Recibo #</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Cuota</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha de Pago</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Método de Pago</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Referencia</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Monto</th>
                <th scope="col" class="relative px-6 py-3 text-right">
                  <span class="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="pay in payments" 
                :key="pay.id" 
                class="hover:bg-slate-50/50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  #{{ pay.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  #{{ pay.order ? pay.order.number_order : 'N/A' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(pay.payment_date || pay.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-capitalize">
                  {{ pay.method ? pay.method.replace('-', ' ') : 'N/A' }}
                  <div v-if="pay.issuer_bank || pay.issuer_phone || pay.issuer_id" class="text-xs text-gray-400 font-normal">
                    <span v-if="pay.issuer_bank">{{ pay.issuer_bank }} </span>
                    <span v-if="pay.issuer_phone">Tél: {{ pay.issuer_phone }} </span>
                    <span v-if="pay.issuer_id">CI: {{ pay.issuer_id }}</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-semibold">
                  {{ pay.reference || 'N/A' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                  ${{ parseFloat(pay.amount).toFixed(2) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold">
                  <a 
                    target="_blank" 
                    :href="`/orders/pay/${pay.id}/pdf-external`" 
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  >
                    <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Descargar Recibo
                  </a>
                </td>
              </tr>
              <tr v-if="!payments || payments.length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-sm text-gray-500 bg-slate-50/50">
                  No hay pagos registrados para esta cuenta aún.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Report instructions alert -->
      <div class="bg-indigo-50 border border-indigo-100 rounded-xl p-5 flex">
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="ml-4">
          <h3 class="text-sm font-bold text-indigo-900">¿Deseas reportar un pago?</h3>
          <p class="text-xs text-indigo-700 mt-1 leading-relaxed">
            Para registrar el comprobante de una cuota pendiente, haz clic sobre el botón verde **Reportar Pago** correspondiente a la cuota. Se desplegarán los detalles de gastos y el formulario para adjuntar tu referencia bancaria y comprobante.
          </p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'

export default {
  components: {
    Head,
    Link,
    Logo,
  },
  props: {
    change: {
      type: Number,
      default: 1
    },
    client: Object,
    orders: Array,
    payments: Array,
    paid_orders_count: Number,
    logo: String,
    is_morochas: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    back() {
      // In public view, go back to state of account query page
      window.location.href = '/estado-cuenta';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const cleanDate = dateString.replace('T', ' ').split(' ')[0];
        const parts = cleanDate.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    }
  }
}
</script>
